/**
 * Server restore for an imported `.fig` source workspace.
 *
 * ─── The defect this closes ──────────────────────────────────────────────
 * Until now an imported source existed ONLY in the importing browser:
 *   • the workspace JSON lives in `localStorage`
 *     (`openpencil-source-workspace-storage.ts`, and only for a `server`
 *     project does it also reach `studio_projects.settings`), and
 *   • the workspace route was the FIXED literal `/ide/source-workspace`
 *     (`openpencil-source-navigation.ts`), which names no import at all.
 * So a second browser, a second device, a private window, or a cleared
 * storage bucket produced an empty workspace, and a link sent to a colleague
 * opened *their* workspace, never the sender's. The product's answer was
 * "re-import the app".
 *
 * ─── Why no new storage is needed ────────────────────────────────────────
 * The source bytes, the surface previews AND the derived bundle are ALREADY
 * durably stored server-side by the import pipeline — this module only
 * recovers the handle that pointed at them, which the bundle has been
 * carrying the whole time in `sourceArtifactRef`:
 *
 *   production  `r2://cogo-openpencil-sources/sources/{workspaceId}/{jobId}/source.fig`
 *               → R2 bucket `cogo-openpencil-sources`
 *                 (apps/cogo-openpencil-import-api/src/index.ts:213,1289)
 *               → readable at `GET /v1/imports/{jobId}/bundle`, authorized by
 *                 `principalForJob` (index.ts:172) which pins
 *                 `job.userId === principal.userId`.
 *
 *   local dev   `local-artifact://openpencil/{sourceId}/{fileName}`
 *               → `.openpencil-figma-imports/{sourceId}/openpencil-figma-source-bundle.v2.json`
 *                 written by vite-plugins/openpencil-figma-import-api.ts
 *               → readable at `GET /api/openpencil/figma/artifacts/{sourceId}/bundle`.
 *
 * Nothing here uploads, copies, derives, or republishes design data. The
 * request body is empty in both modes — a restore is a GET keyed by an
 * opaque id — so no rights-declaration capability is exercised and no
 * admission pin (`ontologyExtraction` / `packDerivation` / … are `const:
 * false` literals in packages/cogo-schema/v4/*) is touched. The reader is the
 * same authenticated principal that uploaded the source; the import worker
 * re-checks ownership on every read.
 *
 * ─── What a restore does and does NOT bring back ─────────────────────────
 * It brings back the IMPORT: the parsed document, its pages, surface
 * candidates, tokens and preview manifest — everything the 2.6 MB upload and
 * the runner pass produced. It does NOT bring back review DECISIONS
 * (page/section/surface/token verdicts), because those are only ever written
 * to this browser. `describeOpenPencilSourceDurability` states exactly that,
 * and the source surface renders it verbatim rather than implying more.
 */

import { getStudioAccessToken } from "@/services/auth-provider";
import {
  normalizeOpenPencilFigmaSourceBundle,
  type OpenPencilFigmaSourceBundle,
} from "./openpencil-figma-source";

/** Mirrors `apiBaseUrl()` in openpencil-figma-client.ts (same env contract). */
function importApiBaseUrl(): string | null {
  const value = (import.meta.env as Record<string, string | undefined>)
    .VITE_OPENPENCIL_IMPORT_API_URL?.trim();
  return value ? value.replace(/\/$/, "") : null;
}

/**
 * The dev adapter's artifact surface. Same prefix the production proxy
 * reserves for the legacy adapter routes, so the two can never collide.
 */
const LOCAL_ARTIFACT_BASE = "/api/openpencil/figma/artifacts";

/**
 * A restore handle is an opaque id plus the mode that can dereference it.
 * `remote` ids are import-job ids; `local` ids are dev-adapter source ids.
 */
export type OpenPencilSourceRestoreHandle =
  | { mode: "remote"; id: string }
  | { mode: "local"; id: string }
  | { mode: "appliance"; id: string };

/**
 * Ids reach us from a URL path segment, so they are attacker-controlled.
 * Both producers mint slug-safe ids, so we accept only that alphabet and a
 * bounded length — this is what keeps a crafted segment from walking out of
 * the artifact prefix or being smuggled into the upstream path.
 *
 * The leading character admits `_` as well as alphanumerics: the dev adapter
 * slugifies the source NAME, and a file whose name begins with non-ASCII —
 * `[공통] 영업점검색_UI.fig`, the real audited source — slugifies to
 * `_ui-9bc850deace0`. Rejecting that made every Korean-named import report
 * itself unrestorable, which is a correctness bug, not a hardening win. `.`
 * is deliberately still barred from the first position, and `..` anywhere is
 * rejected outright below, so traversal remains impossible.
 */
const RESTORE_ID_RE = /^[A-Za-z0-9_][A-Za-z0-9._-]{0,127}$/;

export function isOpenPencilSourceRestoreId(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (value.includes("..")) return false;
  return RESTORE_ID_RE.test(value);
}

/**
 * Recover the durable handle a bundle is carrying.
 *
 * Returns null when the bundle names no server artifact — a bundle built
 * entirely in the browser, or a blocked import that never produced one. A
 * null handle is the honest signal that this import is NOT restorable, and
 * every caller renders that rather than pretending otherwise.
 */
export function readOpenPencilSourceRestoreHandle(
  bundle: Pick<OpenPencilFigmaSourceBundle, "sourceArtifactRef"> | null | undefined,
): OpenPencilSourceRestoreHandle | null {
  const ref = bundle?.sourceArtifactRef;
  if (typeof ref !== "string" || !ref) return null;

  // r2://<bucket>/sources/<workspaceId>/<jobId>/source.fig
  const remote = /^r2:\/\/[^/]+\/sources\/[^/]+\/([^/]+)\/[^/]+$/.exec(ref);
  if (remote && isOpenPencilSourceRestoreId(remote[1])) {
    return { mode: "remote", id: remote[1] };
  }

  // local-artifact://openpencil/<sourceId>/<fileName>
  const local = /^local-artifact:\/\/openpencil\/([^/]+)\/[^/]+$/.exec(ref);
  if (local && isOpenPencilSourceRestoreId(local[1])) {
    return { mode: "local", id: local[1] };
  }

  // cogo-appliance://openpencil-sources/<jobId>/source.fig
  //
  // The on-box importer's spelling. Without this branch an appliance import
  // parsed to null and reported `restorable: false`, so the file vanished on
  // reload even though the box was still storing it -- measured 2026-08-24.
  // The id is the importer's job id, which is exactly what
  // `GET /v1/imports/<id>/bundle` takes, so no mode-specific fetch is needed.
  const appliance =
    /^cogo-appliance:\/\/openpencil-sources\/([^/]+)\/[^/]+$/.exec(ref);
  if (appliance && isOpenPencilSourceRestoreId(appliance[1])) {
    return { mode: "appliance", id: appliance[1] };
  }

  return null;
}

/**
 * The URL segment that names this import. Deliberately just the id: the mode
 * is a property of the CLIENT BUILD (production bundle vs dev server), not of
 * the link, so baking it into the URL would mint two links for one import and
 * make neither portable. A mode mismatch fails closed at fetch time with
 * `not-found`, which is the truthful answer — that import is not reachable
 * from this deployment.
 */
export function openPencilSourceRestoreToken(
  bundle: Pick<OpenPencilFigmaSourceBundle, "sourceArtifactRef"> | null | undefined,
): string | null {
  return readOpenPencilSourceRestoreHandle(bundle)?.id ?? null;
}

export type OpenPencilSourceRestoreFailureReason =
  /** No signed-in Studio user; a restore is always owner-scoped. */
  | "unauthenticated"
  /** The id names no import readable by this user (404/403 are one answer). */
  | "not-found"
  /** The server was reached but could not answer. Retryable. */
  | "unavailable"
  /** Answered, but not with a bundle we can normalize. */
  | "invalid-bundle";

export type OpenPencilSourceRestoreResult =
  | { ok: true; bundle: OpenPencilFigmaSourceBundle }
  | { ok: false; reason: OpenPencilSourceRestoreFailureReason };

const RESTORE_TIMEOUT_MS = 30_000;

/**
 * Fetch the bundle for `id` from whichever import surface this build talks to.
 *
 * Never throws: a restore runs on route load, and an exception there would
 * blank the workspace with no explanation. Every outcome is a typed result the
 * caller can render.
 */
export async function fetchOpenPencilSourceBundleById(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<OpenPencilSourceRestoreResult> {
  if (!isOpenPencilSourceRestoreId(id)) return { ok: false, reason: "not-found" };

  const base = importApiBaseUrl();
  const url = base
    ? `${base}/v1/imports/${encodeURIComponent(id)}/bundle`
    : `${LOCAL_ARTIFACT_BASE}/${encodeURIComponent(id)}/bundle`;

  const headers: Record<string, string> = {};
  if (base) {
    // The production import API authenticates the END USER and re-checks that
    // the job belongs to them, so a restore is only ever possible for the
    // person who imported it. No token, no restore — we do not fall back to
    // an anonymous read.
    const accessToken = await getStudioAccessToken();
    if (!accessToken) return { ok: false, reason: "unauthenticated" };
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const timeout = new AbortController();
  const timeoutId = setTimeout(
    () => timeout.abort(new DOMException("Restore timed out", "TimeoutError")),
    RESTORE_TIMEOUT_MS,
  );
  const signal = options?.signal
    ? AbortSignal.any([options.signal, timeout.signal])
    : timeout.signal;

  try {
    const response = await fetch(url, { headers, signal });
    if (response.status === 401) return { ok: false, reason: "unauthenticated" };
    // 403 and 404 are deliberately the same answer: "not yours / not there".
    // Distinguishing them would turn this into an existence oracle for other
    // people's import ids.
    if (response.status === 403 || response.status === 404) {
      return { ok: false, reason: "not-found" };
    }
    if (!response.ok) return { ok: false, reason: "unavailable" };

    const payload = (await response.json().catch(() => null)) as unknown;
    if (!payload) return { ok: false, reason: "invalid-bundle" };
    try {
      return { ok: true, bundle: normalizeOpenPencilFigmaSourceBundle(payload) };
    } catch {
      return { ok: false, reason: "invalid-bundle" };
    }
  } catch {
    return { ok: false, reason: "unavailable" };
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface OpenPencilSourceDurability {
  /** True when this import can be reopened from another browser/device. */
  restorable: boolean;
  /** The URL segment that names it, when restorable. */
  token: string | null;
}

/**
 * The single place that decides what we may TELL the creator about where their
 * import lives. Derived from the bundle's own artifact ref — never from a
 * hopeful default — so the badge cannot claim durability the server does not
 * actually provide.
 */
export function describeOpenPencilSourceDurability(
  bundle: Pick<OpenPencilFigmaSourceBundle, "sourceArtifactRef"> | null | undefined,
): OpenPencilSourceDurability {
  const token = openPencilSourceRestoreToken(bundle);
  return { restorable: Boolean(token), token };
}
